<template>
  <div v-if="alert && alert.isTriggered" :class="'formAlert ' + bgClass" @click="scrollToField">
    <FontAwesomeIcon class="formAlert__icon" :icon="'fa-light fa-' + icon" />
    <div class="formAlert__body">
      <div class="formAlert__title">{{ alert.shortLabel }}</div>
      <div v-if="isFull" class="formAlert__description" v-html="shortDescription"></div>
      <div v-if="isMinimal" class="formAlert__fieldName">Champ : {{ alert.fieldName }}</div>
      <button type="button" class="formAlert__seeMore" v-if="isFull && seeMoreBtn" @click="seeMore">
        {{ $t("registration.alert.see_more") }}
      </button>
    </div>
  </div>
</template>

<script>
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { useRegistrationStore } from "@/store/registration/registration";
import { mapActions } from "pinia";
import { notificationManager } from "@/services/utilities/notification-manager";
import { i18n } from "@/i18n";

export default {
  name: "FormAlertComponent",
  components: { FontAwesomeIcon },
  props: {
    alert: {
      type: Object,
      required: true,
    },
    model: {
      type: Object,
      required: false,
    },
    type: {
      type: String,
      required: false,
      default: "full",
    },
  },
  data() {
    return {
      icon: "circle-info",
      bgClass: "-info",
      fieldName: null,
      fieldId: null,
      shortDescription: "",
      itemModel: this.model,
      length: 75,
      seeMoreBtn: false,
      tabId: null,
    };
  },
  emits: ["toggleTab"],
  computed: {
    isFull() {
      return this.type === "full";
    },
    isMinimal() {
      return this.type === "minimal";
    },
    alertType() {
      if (this.alert.level === 3) {
        return "error";
      } else if (this.alert.level === 2) {
        return "warning";
      }

      return "info";
    },
  },
  watch: {},
  mounted() {
    this.init();
  },
  methods: {
    init() {
      this.setShortDescription();
      this.setFieldName();
      this.setFieldId();
      this.setTabId();
      this.set();
      this.pushToStore();
    },

    set() {
      switch (this.alert.level) {
        case 1:
          this.icon = "info-circle";
          this.bgClass = "-info";
          break;
        case 2:
          this.icon = "triangle-exclamation";
          this.bgClass = "-warning";
          break;
        case 3:
          this.icon = "circle-exclamation";
          this.bgClass = "-danger";
          break;
      }
    },

    truncate(str, length) {
      if (str.length <= length) {
        return str;
      }
      return str.slice(0, length) + "...";
    },

    setFieldName() {
      this.fieldName = this.model ? this.model.vars.label : null;
    },

    setTabId() {
      this.tabId = this.$route.params.tab ? this.$route.params.tab : null;
    },

    setShortDescription() {
      this.seeMoreBtn = this.alert.description.length > this.length;

      this.shortDescription = this.seeMoreBtn
        ? this.truncate(this.alert.description, this.length)
        : this.alert.description;
    },

    setFieldId() {
      this.fieldId = this.model ? this.model.vars.id : null;
    },

    pushToStore() {
      if (this.alert.isTriggered && this.fieldName) {
        this.itemModel.vars.isInvalid = true;
        this.pushAlert({ ...this.alert, ...{ fieldName: this.fieldName, fieldId: this.fieldId } });
      }
    },

    scrollToField() {
      if (this.alert.tabId) {
        this.$emit("toggleTab", this.alert.tabId);
      }
      const input = document.getElementById(this.alert.fieldId);
      if (input && this.type === "minimal") {
        const offset = window.innerWidth < 768 ? 300 : 250;
        window.scrollTo({
          behavior: "smooth",
          top: input.getBoundingClientRect().top - document.body.getBoundingClientRect().top - offset,
        });
      }
    },

    seeMore() {
      notificationManager.showAlert(
        this.alertType,
        this.alert.title,
        this.alert.description,
        true,
        false,
        i18n.global.t("close"),
        null,
        "#000",
        "swal-wide",
        true,
      );
    },

    ...mapActions(useRegistrationStore, {
      pushAlert: "pushAlert",
    }),
  },
};
</script>

<style lang="scss" scoped>
.formAlert {
  border-radius: $radius;
  padding: 15px;
  margin-top: 8px;
  position: relative;
  overflow: hidden;
  border-left: 4px solid;
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
  cursor: pointer;

  &.-info {
    background: $light-purple;
    color: $purple;
    border-color: $purple;

    @include dark-theme {
      background: $purple;
      color: $light-purple;
      border-color: $light-purple;
    }
  }

  &.-warning {
    background: $light-orange;
    color: $orange;
    border-color: $orange;

    @include dark-theme {
      background: $orange;
      color: $light-orange;
      border-color: $light-orange;
    }
  }

  &.-danger {
    background: $light-red;
    color: $red;
    border-color: $red;

    @include dark-theme {
      background: $red;
      color: $light-red;
      border-color: $light-red;
    }
  }

  &__icon {
    width: 14px;
    margin-right: 10px;
  }

  &__title {
    line-height: 15px;
    font-weight: 700;
    margin-bottom: 8px;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__body {
    min-width: 0;
  }

  &__fieldName {
    line-height: 12px;
    font-size: 12px;
    margin-top: 5px;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__seeMore {
    background: none;
    border: none;
    font-weight: bold;
    color: inherit;
    text-decoration: underline;
    padding: 0;
    margin-top: 5px;
  }
}
</style>
